import { useNavigate, useParams } from 'react-router-dom';
import { useState, useRef, useCallback } from 'react';
import { AppCard, AppPage } from '../../components/general';
import { BreadcrumbItem } from '../../components/general/AppPage';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { WaterfallEditor } from './WaterfallEditor';
import { useWaterfalls } from '../../hooks/useWaterfalls';
import { IntegrationType, WaterfallVersionViewModel, WaterfallViewModel } from '../../models/waterfall';
import * as waterfallService from '../../services/waterfallService';

const editorBreadcrumbs = (basePath: string, isNew: boolean): BreadcrumbItem[] => {
  const map: Record<string, { label: string; to: string }> = {
    '/credit/waterfalls': { label: 'Credit', to: '/products/credit' },
    '/fees/waterfalls': { label: 'Fees', to: '/products/fees' },
    '/vox/waterfalls': { label: 'VoX', to: '/products/vox' },
    '/auw/waterfalls': { label: 'AUW', to: '/products/auw' },
  };
  const parent = map[basePath];
  if (parent) return [{ label: 'Dashboard', to: '/dashboard' }, parent, { label: 'Waterfalls', to: basePath }, { label: isNew ? 'Create' : 'Edit' }];
  return [{ label: 'Dashboard', to: '/dashboard' }, { label: 'Waterfalls', to: basePath }, { label: isNew ? 'Create' : 'Edit' }];
};

export const WaterfallEditorContainer = ({ basePath = '/waterfalls', defaultIntegrationType }: { basePath?: string; defaultIntegrationType?: IntegrationType }) => {
  const { user, isLoading: authIsLoading } = useAuth();
  const navigate = useNavigate();
  const { waterfallId } = useParams<{ waterfallId?: string }>();
  const id = waterfallId ? Number(waterfallId) : undefined;
  const toastRef = useRef<Toast>(null);

  const { waterfallDetailQuery, waterfallsQuery, createWaterfall, updateWaterfall, createVersion, updateVersion, deleteVersion, setDisabledStatus } =
    useWaterfalls(id);

  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const [versionIdsWithExecutionRecords, setVersionIdsWithExecutionRecords] = useState<number[]>([]);
  const [isLoadingExecutionCheck, setIsLoadingExecutionCheck] = useState(false);

  const fetchExecutionRecords = useCallback(async () => {
    if (!id) return;
    setIsLoadingExecutionCheck(true);
    try {
      const ids = await waterfallService.getVersionIdsWithExecutionRecords(id);
      setVersionIdsWithExecutionRecords(ids);
    } catch (error) {
      console.error('Failed to fetch execution records:', error);
    } finally {
      setIsLoadingExecutionCheck(false);
    }
  }, [id]);

  const isLoading = authIsLoading || !user || (id && waterfallDetailQuery.isLoading);

  const onCreate = async ({ name, integrationType }: any) => {
    try {
      const created = await createWaterfall({ name, integrationType });
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Waterfall created successfully',
        life: 3000,
      });
      
      if (created.waterfallId) navigate(`${basePath}/edit/${created.waterfallId}`);
    } catch (error: any) {
      console.error('Failed to create waterfall:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to create waterfall';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
    }
  };

  const onUpdate = async (data: Partial<WaterfallViewModel>) => {
    if (!id || !waterfallDetailQuery.data) return;

    const existing = waterfallDetailQuery.data;

    // Only send fields that the API accepts
    const payload = {
      waterfallId: id,
      name: data.name ?? existing.name ?? '',
      integrationType: data.integrationType ?? existing.integrationType ?? IntegrationType.None,
      currentVersionId: existing.currentVersionId ?? null,
      disabled: existing.disabled ?? false,
      archived: existing.archived ?? false,
    };

    try {
      await updateWaterfall(payload);
      await waterfallDetailQuery.refetch();
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Waterfall updated successfully',
        life: 3000,
      });
    } catch (error: any) {
      console.error('Failed to update waterfall:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to update waterfall';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });

      throw error;
    }
  };

  const onCreateVersion = async (source: WaterfallVersionViewModel) => {
    if (!id) return;
    
    try {
        const newVersion = await createVersion({
          waterfallId: id,
          executionMode: source.executionMode,
          initialSteps: source.initialSteps,
          initialStepsExecutionMode: source.initialStepsExecutionMode,
          refreshOrderSettings: source.refreshOrderSettings,
          alwaysSendParentWaterfallDetailInWebhookEvents: source.alwaysSendParentWaterfallDetailInWebhookEvents,
        });
      await waterfallDetailQuery.refetch();
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Version ${newVersion.versionNumber} created successfully`,
        life: 3000,
      });
      
      if (newVersion?.waterfallVersionId) setSelectedVersionId(newVersion.waterfallVersionId);
    } catch (error: any) {
      console.error('Failed to create version:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to create version';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
      
      await waterfallDetailQuery.refetch();
    }
  };

  const onSaveVersion = async (version: WaterfallVersionViewModel) => {
    if (!id) return;

    const payload = {
      waterfallVersionId: version.waterfallVersionId,
      waterfallId: id,
      executionMode: version.executionMode,
      initialSteps: version.initialSteps,
      initialStepsExecutionMode: version.initialStepsExecutionMode,
      refreshOrderSettings: version.refreshOrderSettings,
      alwaysSendParentWaterfallDetailInWebhookEvents: version.alwaysSendParentWaterfallDetailInWebhookEvents,
    };

    try {
      await updateVersion(payload);
      await waterfallDetailQuery.refetch();
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Waterfall version saved successfully',
        life: 3000,
      });
    } catch (error: any) {
      console.error('Failed to save waterfall version:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to save waterfall version';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
      
      throw error;
    }
  };

  const onSetActiveVersion = async (waterfallId: number, waterfallVersionId: number) => {
    try {
      await updateWaterfall({
        waterfallId,
        name: waterfallDetailQuery.data?.name ?? '',
        integrationType: waterfallDetailQuery.data?.integrationType ?? IntegrationType.None,
        currentVersionId: waterfallVersionId,
      });
      waterfallDetailQuery.refetch();
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Active version updated successfully',
        life: 3000,
      });
    } catch (error: any) {
      console.error('Failed to set active version:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to set active version';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
      
      throw error;
    }
  };

  const onDeleteVersion = async (versionId: number) => {
    if (!id) return;
    
    try {
      await deleteVersion({ waterfallId: id, waterfallVersionId: versionId });
      waterfallDetailQuery.refetch();
      
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Version deleted successfully',
        life: 3000,
      });
    } catch (error: any) {
      console.error('Failed to delete version:', error);
      
      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to delete version';
      
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
      
      throw error;
    }
  };

  const onToggleDisabled = async (waterfallId: number, disabled: boolean) => {
    try {
      await setDisabledStatus({ waterfallId, disabled });
      await waterfallDetailQuery.refetch();

      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `Waterfall ${disabled ? 'disabled' : 'enabled'} successfully`,
        life: 3000,
      });
    } catch (error: any) {
      console.error('Failed to toggle waterfall status:', error);

      const errorMessage = error?.response?.data?.errors || error?.message || 'Failed to update waterfall status';

      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000,
      });
    }
  };

  const onCancel = () => navigate(basePath);

  if (isLoading) {
    return (
      <AppPage title="Waterfall Editor" breadcrumbs={editorBreadcrumbs(basePath, !id)}>
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-2">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span>Loading Waterfall...</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <>
      <Toast ref={toastRef} />
      <WaterfallEditor
        waterfall={waterfallDetailQuery.data ?? null}
        onSubmit={id ? onUpdate : onCreate}
        onCancel={onCancel}
        onCreateVersion={onCreateVersion}
        onSetActiveVersion={onSetActiveVersion}
        onSaveVersion={onSaveVersion}
        onDeleteVersion={onDeleteVersion}
        onToggleDisabled={onToggleDisabled}
        initialSelectedVersion={selectedVersionId}
        allWaterfalls={waterfallsQuery.data ?? []}
        defaultIntegrationType={defaultIntegrationType}
        basePath={basePath}
        versionIdsWithExecutionRecords={versionIdsWithExecutionRecords}
        isLoadingExecutionCheck={isLoadingExecutionCheck}
        onOpenPublications={fetchExecutionRecords}
      />
    </>
  );
};
